import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import '../../../styles/userTesting/index.css';

/**
 * Delete confirmation modal for session or project deletion
 */
function UTDeleteModal({
  isOpen,
  count = 1,
  type = 'session',
  onConfirm,
  onCancel,
  loading = false
}) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const isProject = type === 'project';
  const isDraft = type === 'draft';
  const isMultiple = count > 1;

  const title = isDraft
    ? 'Discard this draft?'
    : isProject
      ? 'Delete this test?'
      : `Delete ${isMultiple ? `${count} sessions` : 'session'}?`;

  const description = isDraft
    ? 'This in-progress draft will be removed. It has no live link and no sessions.'
    : isProject
      ? 'Are you sure you want to delete this test? All sessions, recordings, transcripts, and AI insights will be permanently deleted.'
      : isMultiple
        ? `Are you sure you want to delete these ${count} testing sessions? This will permanently remove all audio recordings, transcripts, and AI analysis for these sessions.`
        : 'Are you sure you want to delete this testing session? This will permanently remove the audio recording, transcript, and AI analysis.';

  const buttonLabel = isDraft
    ? 'Discard draft'
    : isProject
      ? 'Delete Test'
      : `Delete ${isMultiple ? 'Sessions' : 'Session'}`;

  return ReactDOM.createPortal(
    <div className="ut-modal-root ut-modal-overlay ut-theme" onClick={onCancel}>
      <div className="ut-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ut-modal-header">
          <h2 className="ut-modal-title">{title}</h2>
          <button className="ut-modal-close" onClick={onCancel}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="ut-modal-body">
          <div className="ut-delete-modal-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
          </div>
          <p className="ut-delete-modal-text">{description}</p>
          <p className="ut-delete-modal-warning">
            This action cannot be undone.
          </p>
        </div>

        <div className="ut-modal-footer">
          <button
            className="ut-btn ut-btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="ut-btn ut-btn-danger"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? (isDraft ? 'Discarding...' : 'Deleting...') : buttonLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default UTDeleteModal;
